import React, { useState } from 'react'
import { Box, Collapse, Tooltip, Typography } from '@mui/material'
import { PILL_RADIUS } from '../../agent'

import {
  citationReferenceFilter,
  citationTooltip,
  type CitationNumbering,
  type CitationReference,
} from '../../utils/citations'

// The reader-facing half of numbered citations (#1296). Parsing and numbering
// live in utils/citations.ts; this file is the ONE marker and the ONE reference
// list, so the Gen AI card, the executive report and the agent chat cannot
// drift apart.

export interface CitationMarkerProps {
  reference: CitationReference
  /** Opens the cited table (and highlights the cited cell, when there is one). */
  onSelect: (reference: CitationReference) => void
}

/**
 * Visual-only styling for the numbered pill — shared by the inline marker
 * and the reference-list badge. Interactive/layout additions (cursor, hover,
 * font-size, margins) belong to each caller, layered on top.
 */
const citationPillSx = {
  display: 'inline-block',
  fontWeight: 600,
  px: '6px',
  borderRadius: PILL_RADIUS,
  lineHeight: 1.6,
  bgcolor: '#E3F2FD',
  color: '#1565C0',
  border: '1px solid #BBDEFB',
} as const

/**
 * The number in the flow of the text, in the small blue pill the old
 * table-name chips used (Adam, 8 Sep): the table name is long and
 * descriptive now, so it belongs in the tooltip, not inline.
 */
export const CitationMarker: React.FC<CitationMarkerProps> = ({ reference, onSelect }) => (
  <Tooltip title={citationTooltip(reference)} arrow placement="top">
    <Box
      component="span"
      role="button"
      tabIndex={0}
      data-testid="citation-marker"
      aria-label={`Open ${citationTooltip(reference)}`}
      onClick={() => onSelect(reference)}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect(reference)
        }
      }}
      sx={{
        ...citationPillSx,
        fontSize: '0.7em',
        mx: '2px',
        verticalAlign: 'text-bottom',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        userSelect: 'none',
        '&:hover': { bgcolor: '#BBDEFB', borderColor: '#90CAF9' },
      }}
    >
      {reference.number}
    </Box>
  </Tooltip>
)

/**
 * Render one run of text with its citations replaced by numbered markers.
 * Returns the original string untouched when it carries no citation, so a text
 * node without one is not wrapped in anything new.
 */
export function renderCitationText(
  text: string,
  numbering: CitationNumbering,
  onSelect: (reference: CitationReference) => void,
  keyPrefix = 'citation',
): React.ReactNode {
  const segments = numbering.parse(text)
  if (segments.every(s => s.kind === 'text')) return text
  return segments.map((segment, index) =>
    segment.kind === 'text' ? (
      <React.Fragment key={`${keyPrefix}-text-${index}`}>{segment.text}</React.Fragment>
    ) : (
      <CitationMarker key={`${keyPrefix}-${index}`} reference={segment.reference} onSelect={onSelect} />
    ),
  )
}

export interface CitationReferenceListProps {
  references: CitationReference[]
  /** The filter the cited table ran under — resolved by the host, which knows
   *  which job the table belongs to. */
  filterFor?: (reference: CitationReference) => { filter: string; filterDropped: boolean }
  onSelect: (reference: CitationReference) => void
}

/**
 * "References" sits behind a side arrow (Adam, 8 Sep): a caption row with a
 * chevron and the count, closed by default, that opens the list of cited
 * tables, one line each, every line opening the same modal.
 */
export const CitationReferenceList: React.FC<CitationReferenceListProps> = ({ references, filterFor, onSelect }) => {
  const [open, setOpen] = useState(false)
  if (references.length === 0) return null
  return (
    <Box sx={{ mt: 1.5 }} data-testid="citation-references">
      <Box
        component="button"
        type="button"
        data-testid="citation-references-toggle"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          p: 0,
          border: 'none',
          background: 'none',
          font: 'inherit',
          cursor: 'pointer',
          color: 'text.secondary',
          '&:hover': { color: 'info.main' },
        }}
      >
        <Box
          component="span"
          aria-hidden
          sx={{
            display: 'inline-block',
            fontSize: 11,
            lineHeight: 1,
            transition: 'transform 150ms ease',
            transform: open ? 'rotate(90deg)' : 'none',
          }}
        >
          ▶
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          References ({references.length})
        </Typography>
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ mt: 0.25 }}>
          {references.map((reference) => {
            const resolved = filterFor?.(reference)
            return (
              <Typography
                key={reference.number}
                variant="caption"
                component="div"
                data-testid={`citation-reference-${reference.number}`}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(reference)}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelect(reference)
                  }
                }}
                sx={{
                  color: 'text.secondary',
                  cursor: 'pointer',
                  lineHeight: 1.5,
                  '&:hover': { color: 'info.main' },
                }}
              >
                <Box component="span" sx={{ ...citationPillSx, fontSize: '0.85em', mr: 0.75 }}>{reference.number}</Box>
                {reference.label}
                {' — '}
                {citationReferenceFilter(resolved?.filter, resolved?.filterDropped)}
              </Typography>
            )
          })}
        </Box>
      </Collapse>
    </Box>
  )
}
